import React from 'react';
import { Input } from 'reactstrap';
import {
    NonCompetitionRestrictionSlot,
    NonCompetitionRestrictionSlotLabels,
} from '../../../../../../slices/marketplace/loi-form/non-competition-restriction-slot';
import { LegalIssueItem } from '../../../../../../slices/marketplace/loi-form/legal-issue-item';
import InfoTooltip from '../../../../../../components/info-tooltip/info-tooltip';
import './TermsFormNonCompetitionBlock.scss';

const NCR_LABEL_TOOLTIPS = {
    distanceRadiusMiles: 'This value is taken from your input in the legal term guidance tab, under the Asset Purchase Agreement section. Editing the value there will change the value shown here.',
    durationMonthsFromClosing: 'This value is taken from your input in the legal term guidance tab, under the Asset Purchase Agreement section. Editing the value there will change the value shown here.',
    durationMonthsFromEmployment: 'This value is taken from your input in the legal term guidance tab, under the Employment agreement section. Editing the value there will change the value shown here.',
} as const;

function getItemForSlot(
    legalIssues: LegalIssueItem[],
    additionalCovenants: LegalIssueItem[],
    slot: NonCompetitionRestrictionSlot
): LegalIssueItem | undefined {
    return (
        legalIssues.find((i) => i.linkedNonCompetitionRestrictionSlot === slot) ??
        additionalCovenants.find((i) => i.linkedNonCompetitionRestrictionSlot === slot)
    );
}

function getValueForSlot(
    legalIssues: LegalIssueItem[],
    additionalCovenants: LegalIssueItem[],
    slot: NonCompetitionRestrictionSlot
): string | number | undefined {
    const item = getItemForSlot(legalIssues, additionalCovenants, slot);
    if (!item?.value) return undefined;
    if (slot === NonCompetitionRestrictionSlot.DistanceRadiusMiles) {
        const n = Number(item.value);
        return Number.isFinite(n) ? n : item.value;
    }
    return item.value;
}

export interface TermsFormNonCompetitionBlockProps {
    legalIssues: LegalIssueItem[];
    additionalCovenants?: LegalIssueItem[];
    readOnly?: boolean;
}

export const TermsFormNonCompetitionBlock: React.FC<TermsFormNonCompetitionBlockProps> = ({
    legalIssues,
    additionalCovenants = [],
}) => {
    return (
        <div className="terms-form-detail-block terms-form-section-underlined terms-form-non-competition-block">
            <div className="terms-form-block-name">Non-Competition Restriction</div>
            <div className="terms-form-form-content">
                <div className="terms-form-non-competition-row">
                    <div className="terms-form-non-competition-field">
                        <span className="terms-form-non-competition-label">
                            {NonCompetitionRestrictionSlotLabels[NonCompetitionRestrictionSlot.DistanceRadiusMiles]}
                            <InfoTooltip text={NCR_LABEL_TOOLTIPS.distanceRadiusMiles} idText="ncr-distance" />
                        </span>
                        <div className="terms-form-non-competition-value">
                            <Input
                                type="number"
                                value={
                                    getValueForSlot(
                                        legalIssues,
                                        additionalCovenants,
                                        NonCompetitionRestrictionSlot.DistanceRadiusMiles
                                    ) ?? ''
                                }
                                className="form-control loi-form-input"
                                aria-label="Distance Radius Miles"
                                disabled
                            />
                        </div>
                    </div>
                    <div className="terms-form-non-competition-field">
                        <span className="terms-form-non-competition-label">
                            {NonCompetitionRestrictionSlotLabels[NonCompetitionRestrictionSlot.DurationYearsFromClosing]}
                            <InfoTooltip text={NCR_LABEL_TOOLTIPS.durationMonthsFromClosing} idText="ncr-duration-closing" />
                        </span>
                        <div className="terms-form-non-competition-value">
                            <Input
                                type="text"
                                value={
                                    getValueForSlot(
                                        legalIssues,
                                        additionalCovenants,
                                        NonCompetitionRestrictionSlot.DurationYearsFromClosing
                                    ) ?? ''
                                }
                                className="form-control loi-form-input"
                                aria-label="Duration Months from Closing"
                                disabled
                            />
                        </div>
                    </div>
                    <div className="terms-form-non-competition-field">
                        <span className="terms-form-non-competition-label">
                            {NonCompetitionRestrictionSlotLabels[NonCompetitionRestrictionSlot.DurationYearsFromEmployment]}
                            <InfoTooltip text={NCR_LABEL_TOOLTIPS.durationMonthsFromEmployment} idText="ncr-duration-employment" />
                        </span>
                        <div className="terms-form-non-competition-value">
                            <Input
                                type="text"
                                value={
                                    getValueForSlot(
                                        legalIssues,
                                        additionalCovenants,
                                        NonCompetitionRestrictionSlot.DurationYearsFromEmployment
                                    ) ?? ''
                                }
                                className="form-control loi-form-input"
                                aria-label="Duration Months from Employment"
                                disabled
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
